import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Send, Inbox, MailCheck, Reply, AlertCircle, Rocket, Mail,
    LifeBuoy, CheckCircle2, AlertTriangle, Zap, Pause,
    Play, RefreshCw, Package, ClipboardList, ArrowUpFromLine,
    ShieldCheck, XCircle, Link2, X, Flame
} from 'lucide-react';

const API = process.env.REACT_APP_ACQUISITION_API || 'http://localhost:5500';

function getToken(): string {
    try { return JSON.parse(localStorage.getItem('acq_auth') || '{}').token || ''; }
    catch { return ''; }
}
function authHeaders(): Record<string, string> {
    return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` };
}

interface WarmupAccount {
    id: string; email: string; provider: string; status: string;
    warmup_score: number; last_error: string | null; created_at: string;
    import_batch_id: string | null; import_batch_name?: string | null; hasOAuthToken?: boolean;
}
interface ImportBatch { id: string; name: string; created_at: string; account_count: number; ready_count: number; }
interface WarmupOptions { mode: 'domain' | 'outlook_pool'; batchId: string | null; dailyLimit: number; }
interface PoolPreview { eligibleAccounts: number; plannedMessages: number; message: string; pairs: { from: string; to: string }[]; }
interface WarmupLog {
    id: string; account_id: string; sender_email: string;
    action: string; details: string | null; created_at: string;
}
interface WarmupStats {
    sentToday: number; receivedToday: number; rescuedToday: number;
    repliedToday: number; totalAccounts: number; activeAccounts: number;
}
interface CycleState {
    options?: WarmupOptions;
    running: boolean;
    startedAt: string | null;
    currentPair: number;
    totalPairs: number;
    phase: string;
    currentSender: string;
    currentOutlook: string;
    lastResult: { sent: number; rescued: number; replied: number; errors: number; finishedAt: string; message?: string } | null;
}
const ACTION_LABELS: Record<string, string> = { sent: '已提交', received: '已收到', replied: '已回复', rescued: '已移出', error: '失败', pool_pending: '待核查', pool_unknown: '结果不明', pool_failed: '发送失败' };
const ACTION_COLORS: Record<string, string> = { pool_pending: '#fbbf24', pool_failed: '#f87171', pool_unknown: '#fbbf24', sent: '#6ee7b7', rescued: '#a5b4fc', replied: '#fbbf24', received: '#6ee7b7', error: '#f87171' };
const ActionIcon = ({ action }: { action: string }) => {
    const size = 14;
    switch (action) {
        case 'sent': return <Send size={size} color="#6ee7b7" />;
        case 'rescued': return <Inbox size={size} color="#a5b4fc" />;
        case 'replied': return <Reply size={size} color="#fbbf24" />;
        case 'received': return <MailCheck size={size} color="#6ee7b7" />;
        case 'error': return <XCircle size={size} color="#f87171" />;
        default: return <Mail size={size} color="#94a3b8" />;
    }
};

/* ─── Styles ─── */
const card: React.CSSProperties = {
    background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
    borderRadius: 12, padding: 20,
};
const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
    background: 'var(--surface-default)', color: 'var(--text-primary)',
    border: '1px solid var(--border-default)', borderRadius: 6, outline: 'none',
};
const smallBtn = (bg: string, color: string): React.CSSProperties => ({
    background: `${bg}18`, color, border: `1px solid ${bg}44`,
    borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
});

export function WarmupPage() {
    const [accounts, setAccounts] = useState<WarmupAccount[]>([]);
    const [batches, setBatches] = useState<ImportBatch[]>([]);
    const [batchFilter, setBatchFilter] = useState('all');
    const [accountSearch, setAccountSearch] = useState('');
    const [batchName, setBatchName] = useState('');
    const [options, setOptions] = useState<WarmupOptions>(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('outlook_warmup_options') || 'null');
            if (saved && (saved.batchId === null || typeof saved.batchId === 'string')) return { mode: 'outlook_pool', batchId: saved.batchId, dailyLimit: Number.isInteger(saved.dailyLimit) && saved.dailyLimit >= 1 && saved.dailyLimit <= 10 ? saved.dailyLimit : 2 };
        } catch {}
        return { mode: 'outlook_pool', batchId: null, dailyLimit: 2 };
    });
    const optionsInitialized = useRef(false);
    const [senderSearch, setSenderSearch] = useState('');
    const [preview, setPreview] = useState<PoolPreview | null>(null);
    const [previewError, setPreviewError] = useState('');
    const [actionError, setActionError] = useState('');
    const [actionBusy, setActionBusy] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [logs, setLogs] = useState<WarmupLog[]>([]);
    const [stats, setStats] = useState<WarmupStats | null>(null);
    const [cycle, setCycle] = useState<CycleState>({ running: false, startedAt: null, currentPair: 0, totalPairs: 0, phase: 'idle', currentSender: '', currentOutlook: '', lastResult: null });
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState<string | null>(null);
    const [tab, setTab] = useState<'senders' | 'pool' | 'import' | 'logs'>('senders');
    const [outlookCapacity, setOutlookCapacity] = useState<{ totalAccounts: number; availableAccounts: number; totalCapacity: number; remainingCapacity: number } | null>(null);

    // Add form
    const [newEmail, setNewEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [addError, setAddError] = useState('');

    // Bulk import
    const [importText, setImportText] = useState('');
    const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: string[]; errorCount?: number; batch?: { id: string; name: string } | null } | null>(null);
    const [importing, setImporting] = useState(false);

    const fetchAll = useCallback(async () => {
        // Skip fetch when browser is offline
        if (!navigator.onLine) return;
        try {
            const [accRes, statsRes, logsRes, capRes, batchRes] = await Promise.all([
                fetch(`${API}/warmup/accounts`, { headers: authHeaders() }),
                fetch(`${API}/warmup/stats`, { headers: authHeaders() }),
                fetch(`${API}/warmup/logs?limit=50`, { headers: authHeaders() }),
                fetch(`${API}/dispatch/capacity`, { headers: authHeaders() }).catch(() => null),
                fetch(`${API}/warmup/batches`, { headers: authHeaders() }),
            ]);
            if (![accRes, statsRes, logsRes, batchRes].every(res => res.ok)) throw new Error('加载失败');
            setLoadError('');
            const loadedBatches: ImportBatch[] = (await batchRes.json()).batches || [];
            setBatches(loadedBatches);
            if (!optionsInitialized.current) {
                let hasSaved = false;
                try { const saved = JSON.parse(localStorage.getItem('outlook_warmup_options') || 'null'); hasSaved = !!saved && (saved.batchId === null || saved.batchId === 'legacy' || loadedBatches.some(b => b.id === saved.batchId)); } catch {}
                if (!hasSaved) setOptions(current => ({ ...current, mode: 'outlook_pool', batchId: null }));
                optionsInitialized.current = true;
            }
            setAccounts((await accRes.json()).accounts || []);
            const sd = await statsRes.json();
            setStats(sd.stats || null); setEnabled(sd.enabled || false);
            if (sd.cycle) setCycle(sd.cycle);
            if (sd.enabled && sd.options?.mode === 'outlook_pool') setOptions(sd.options);
            else if (sd.cycle?.running && sd.cycle.options?.mode === 'outlook_pool') setOptions(sd.cycle.options);
            setLogs((await logsRes.json()).logs || []);
            if (capRes) {
                const capData = await capRes.json();
                if (capData.success) setOutlookCapacity(capData);
            }
        } catch {
            setLoadError("数据刷新失败，请检查本地服务后重试。");
        }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchAll();
        // Poll faster (5s) when cycle is running, otherwise every 30s
        const interval = cycle.running ? 5000 : 30000;
        const t = setInterval(fetchAll, interval);
        // Re-fetch immediately when coming back online
        const onOnline = () => fetchAll();
        window.addEventListener('online', onOnline);
        return () => { clearInterval(t); window.removeEventListener('online', onOnline); };
    }, [fetchAll, cycle.running]);

    useEffect(() => {
        setPreview(null); setPreviewError('');
        if (options.mode !== 'outlook_pool') return;
        const controller = new AbortController();
        fetch(`${API}/warmup/preview`, { method: 'POST', headers: authHeaders(),
            body: JSON.stringify(options), signal: controller.signal })
            .then(async res => { const data = await res.json(); if (!data.success) throw new Error(data.error); return data.preview; })
            .then(data => setPreview(data))
            .catch(error => { if (!controller.signal.aborted) setPreviewError(error.message || '无法加载预热配对'); });
        return () => controller.abort();
    }, [options, accounts]);

    useEffect(() => {
        if (loading || !optionsInitialized.current) return;
        try { localStorage.setItem('outlook_warmup_options', JSON.stringify(options)); } catch {}
    }, [options, loading]);

    async function handleAdd(e: React.FormEvent) {
        e.preventDefault(); setAddError('');
        if (!newEmail || !newPassword) { setAddError("请填写邮箱和密码"); return; }
        try {
            const res = await fetch(`${API}/warmup/accounts`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({ email: newEmail, password: newPassword, provider: 'outlook' }),
            });
            const data = await res.json();
            if (!data.success) { setAddError(data.error || "发送失败"); return; }
            setNewEmail(''); setNewPassword(''); fetchAll();
        } catch { setAddError("网络连接失败"); }
    }

    async function handleDelete(id: string) {
        if (!window.confirm("移除此预热账号？")) return;
        await fetch(`${API}/warmup/accounts/${id}`, { method: 'DELETE', headers: authHeaders() });
        fetchAll();
    }

    async function handleTest(id: string) {
        setTesting(id);
        try {
            const data = await (await fetch(`${API}/warmup/accounts/${id}/test`, { method: 'POST', headers: authHeaders() })).json();
            alert(data.imap && data.smtp ? '✅ IMAP + SMTP OK!' : `❌ ${data.error || "未知错误"}`);
        } catch { alert("网络连接失败"); }
        setTesting(null); fetchAll();
    }

    async function handleRun() {
        setActionBusy(true); setActionError('');
        try {
            const res = await fetch(`${API}/warmup/run`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(options) });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || '启动失败');
            if (data.cycle) setCycle(data.cycle);
            await fetchAll();
        } catch (error) { setActionError(error instanceof Error ? error.message : '网络连接失败'); }
        finally { setActionBusy(false); }
    }

    async function handleToggle() {
        setActionBusy(true); setActionError('');
        try {
            const data = await (await fetch(`${API}/warmup/toggle`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({ ...options, enabled: !(enabled || cycle.running), intervalMinutes: 120 }),
            })).json();
            if (!data.success) throw new Error(data.error || '操作失败');
            setEnabled(data.enabled); await fetchAll();
        } catch (error) { setActionError(error instanceof Error ? error.message : '网络连接失败'); }
        finally { setActionBusy(false); }
    }

    async function handleBulkImport() {
        if (!importText.trim()) return;
        setImporting(true); setImportResult(null);
        try {
            const data = await (await fetch(`${API}/warmup/import`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({ text: importText, batchName }),
            })).json();
            if (!data.success) throw new Error(data.error || '导入失败');
            setImportResult(data);
            if (data.imported > 0) { setImportText(''); setBatchName(''); setBatchFilter(data.batch.id); await fetchAll(); }
        } catch (error) { setImportResult({ imported: 0, skipped: 0, errors: [error instanceof Error ? error.message : "网络连接失败"] }); }
        setImporting(false);
    }

    function scoreBar(score: number) {
        const color = score >= 70 ? '#6ee7b7' : score >= 40 ? '#fbbf24' : '#f87171';
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 60, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)' }}>
                    <div style={{ width: `${score}%`, height: '100%', borderRadius: 3, background: color, transition: 'width 0.5s' }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 28 }}>{score}</span>
            </div>
        );
    }

    function statusDot(status: string) {
        const c = status === 'active' ? '#6ee7b7' : status === 'error' ? '#f87171' : '#94a3b8';
        return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c, boxShadow: `0 0 6px ${c}66` }} />;
    }

    if (loading) return <div style={{ padding: 40, color: 'var(--text-secondary)' }}>正在加载…</div>;

    const activeCount = accounts.filter(a => a.status === 'active').length;
    const errorCount = accounts.filter(a => a.status === 'error').length;
    const legacyCount = accounts.filter(a => !a.import_batch_id).length;
    const filteredAccounts = accounts.filter(a => (batchFilter === 'all' || (batchFilter === 'legacy' ? !a.import_batch_id : a.import_batch_id === batchFilter)) && a.email.toLowerCase().includes(accountSearch.trim().toLowerCase()));
    const warmupBlocked = options.mode === 'outlook_pool' && (!preview?.plannedMessages || !!previewError);
    const optionsLocked = enabled || cycle.running || actionBusy;
    const selectedAccounts = accounts.filter(a => a.provider === 'outlook' && (!options.batchId || (options.batchId === 'legacy' ? !a.import_batch_id : a.import_batch_id === options.batchId)));
    const warmupSenders = selectedAccounts.filter(a => a.status === 'active' && a.hasOAuthToken);
    const visibleSenders = warmupSenders.filter(a => a.email.toLowerCase().includes(senderSearch.trim().toLowerCase()));
    const selectedBatchName = !options.batchId ? '全部批次' : options.batchId === 'legacy' ? '历史账号 / 单个添加' : batches.find(b => b.id === options.batchId)?.name || '批次已不可用';

    return (
        <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
            {/* ── Header ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 22, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ display: 'inline-flex', padding: 6, borderRadius: 10, background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}>
                            <Flame size={20} color="#fff" />
                        </span>
                        邮箱预热管理
                    </h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        Outlook 账号池内互发 ·{' '}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: enabled ? '#6ee7b7' : '#94a3b8' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: enabled ? '#6ee7b7' : '#94a3b8', boxShadow: enabled ? '0 0 8px #6ee7b766' : 'none' }} />
                            {enabled ? "自动预热中" : "已暂停"}
                        </span>
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleToggle} disabled={actionBusy || (!(enabled || cycle.running) && warmupBlocked)} style={{
                        ...smallBtn(enabled ? '#f87171' : '#6366f1', enabled ? '#f87171' : '#a5b4fc'),
                        padding: '6px 14px', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5,
                    }}>
                        {enabled || cycle.running ? <><Pause size={12} /> 停止预热</> : <><Zap size={12} /> 启动自动预热</>}
                    </button>
                    <button className="btn btn-primary" onClick={handleRun} disabled={actionBusy || enabled || cycle.running || accounts.length === 0 || warmupBlocked}
                        style={{ opacity: cycle.running ? 0.6 : 1, fontSize: 12, padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        {cycle.running ? <><RefreshCw size={12} className="spin" /> 正在运行…</> : <><Play size={12} /> 运行一轮</>}
                    </button>
                </div>
            </div>

            {loadError && <p role="alert" style={{ color: '#f87171' }}>{loadError} <button onClick={fetchAll}>重试</button></p>}
            {actionError && <p role="alert" style={{ color: '#f87171' }}>{actionError}</p>}
            <div style={{ ...card, marginBottom: 18 }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>预热方式</h3>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
                    <label style={{ flex: '1 1 230px', fontSize: 12 }}>模式
                        <select aria-label="预热模式" style={{ ...inputStyle, marginTop: 6 }} value={options.mode} disabled={optionsLocked}
                            onChange={e => setOptions({ ...options, mode: e.target.value as WarmupOptions['mode'] })}>
                            
                            <option value="outlook_pool">Outlook 账号池互相预热</option>
                        </select>
                    </label>
                    {options.mode === 'outlook_pool' && <>
                        <label style={{ flex: '1 1 250px', fontSize: 12 }}>参与批次
                            <select aria-label="预热参与批次" style={{ ...inputStyle, marginTop: 6 }} value={options.batchId || 'all'} disabled={optionsLocked}
                                onChange={e => setOptions({ ...options, batchId: e.target.value === 'all' ? null : e.target.value })}>
                                <option value="all">全部批次（仅跨批次配对）</option>
                                {legacyCount > 0 && <option value="legacy">历史账号 / 单个添加（未分批） · {legacyCount}</option>}
                                {batches.map(b => <option key={b.id} value={b.id}>{b.name} · {b.account_count} 个</option>)}
                            </select>
                        </label>
                        <label style={{ flex: '0 1 150px', fontSize: 12 }}>每账号每日上限
                            <select aria-label="互相预热每日上限" style={{ ...inputStyle, marginTop: 6 }} value={options.dailyLimit} disabled={optionsLocked}
                                onChange={e => setOptions({ ...options, dailyLimit: Number(e.target.value) })}>
                                {Array.from({ length: 10 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1} 封 / 日</option>)}
                            </select>
                        </label>
                    </>}
                </div>
                {options.mode === 'outlook_pool' ? <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 14, lineHeight: 1.8 }}>
                    <strong style={{ color: '#a5b4fc' }}>{preview ? `${preview.eligibleAccounts} 个可参与账号 · 本轮最多 ${preview.plannedMessages} 封` : previewError || '正在计算配对…'}</strong>
                    <div>同批次账号禁止互发，只向其他批次的正常授权账号发送。选择单一批次时仅限制发件方，收件方仍从其他批次配对；历史未分批账号视为同一批次；每轮每账号最多发 1 封，每轮最多 20 封，每 2 小时执行一轮。每日额度按 UTC 统计。</div>
                    <div>选择模式不会发信，点击“运行一轮”或“启动自动预热”后才开始。发送任务占用账号时会跳过；停止后不再提交新邮件。</div>
                    {preview?.message && <div style={{ color: '#fbbf24' }}>{preview.message}</div>}
                    {!!preview?.pairs.length && <details style={{ marginTop: 8 }}><summary style={{ cursor: 'pointer' }}>查看配对示例</summary>{preview.pairs.map((pair, i) => <div key={i}>{pair.from} → {pair.to}</div>)}</details>}
                </div> : <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-dim)' }}>使用已配置的域名邮箱向 Outlook 账号池发送预热邮件。</p>}
            </div>

            {/* ── Cycle Progress Banner ── */}
            {cycle.running && (
                <div style={{
                    marginBottom: 16, padding: '12px 16px', borderRadius: 10,
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(168,85,247,0.08))',
                    border: '1px solid rgba(99,102,241,0.3)',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{
                                display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                                background: '#6ee7b7', boxShadow: '0 0 8px #6ee7b766',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }} />
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#a5b4fc' }}>
                                🔥 正在执行预热
                            </span>
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                            邮箱配对 {cycle.currentPair}/{cycle.totalPairs} · {cycle.phase}
                            {cycle.startedAt && ` · 已运行 ${Math.round((Date.now() - new Date(cycle.startedAt).getTime()) / 60000)} 分钟`}
                        </span>
                    </div>
                    <div style={{ width: '100%', height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', marginBottom: 8 }}>
                        <div style={{
                            width: `${cycle.totalPairs > 0 ? (cycle.currentPair / cycle.totalPairs) * 100 : 0}%`,
                            height: '100%', borderRadius: 3,
                            background: 'linear-gradient(90deg, #6366f1, #a855f7)',
                            transition: 'width 0.5s ease',
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)', display: 'flex', gap: 16 }}>
                        {cycle.currentSender && <span>📤 {cycle.currentSender}</span>}
                        {cycle.currentOutlook && <span>→ 📥 {cycle.currentOutlook}</span>}
                    </div>
                </div>
            )}

            {/* ── Last Cycle Result ── */}
            {!cycle.running && cycle.lastResult && (() => {
                const lr = cycle.lastResult!;
                const isNoPairs = lr.sent === 0 && lr.rescued === 0 && lr.replied === 0 && lr.errors === 0;
                const bgColor = isNoPairs ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.06)';
                const borderColor = isNoPairs ? 'rgba(245,158,11,0.25)' : 'rgba(34,197,94,0.2)';
                const textColor = isNoPairs ? '#fbbf24' : '#6ee7b7';
                const icon = isNoPairs ? '⚠️' : '✅';
                return (
                    <div style={{
                        marginBottom: 16, padding: '10px 16px', borderRadius: 10,
                        background: bgColor, border: `1px solid ${borderColor}`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        <span style={{ fontSize: 12, color: textColor, fontWeight: 600 }}>
                            {icon} {lr.message || (
                                isNoPairs
                                    ? "无可用配对，已达到每日上限。"
                                    : `发送 ${lr.sent} 封 · 移出垃圾箱 ${lr.rescued} 封 · 回复 ${lr.replied} 封`
                            )}
                            {!isNoPairs && lr.errors > 0 && <span style={{ color: '#f87171' }}> · {lr.errors} 项错误</span>}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                            {new Date(lr.finishedAt).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                );
            })()}

            {/* ── Stats Row ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 10 }}>
                {[
                    { label: "发件账号", value: warmupSenders.length, Icon: Rocket, color: '#c4b5fd' },
                    { label: "Outlook 账号池", value: accounts.length, Icon: Mail, color: '#a5b4fc' },
                    { label: "今日已发送", value: stats?.sentToday || 0, Icon: Send, color: '#6ee7b7' },
                    { label: "已移出垃圾箱", value: stats?.rescuedToday || 0, Icon: LifeBuoy, color: '#fbbf24' },
                    { label: "待授权", value: accounts.filter(a => a.status !== 'error' && !a.hasOAuthToken).length, Icon: ArrowUpFromLine, color: '#38bdf8' },
                ].map((s, i) => (
                    <div key={i} style={{ ...card, padding: '14px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                            <span style={{ display: 'inline-flex', padding: 6, borderRadius: 8, background: `${s.color}15` }}>
                                <s.Icon size={16} color={s.color} />
                            </span>
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: s.color, margin: '2px 0' }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                    </div>
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
                {[
                    { label: "已回复", value: stats?.repliedToday || 0, Icon: Reply, color: '#c4b5fd' },
                    { label: "可用", value: activeCount, Icon: CheckCircle2, color: '#6ee7b7' },
                    { label: "异常", value: errorCount, Icon: AlertTriangle, color: errorCount > 0 ? '#f87171' : '#94a3b8' },
                    { label: "参与批次", value: selectedBatchName, Icon: Package, color: '#38bdf8', isText: true },
                    { label: "已授权", value: accounts.filter(a => a.status === 'active' && a.hasOAuthToken).length, Icon: ShieldCheck, color: '#6ee7b7' },
                ].map((s: any, i) => (
                    <div key={i} style={{ ...card, padding: '14px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                            <span style={{ display: 'inline-flex', padding: 6, borderRadius: 8, background: `${s.color}15` }}>
                                <s.Icon size={16} color={s.color} />
                            </span>
                        </div>
                        <div style={{ fontSize: s.isText ? 13 : 24, fontWeight: 700, color: s.color, margin: '2px 0' }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* ── Tab Bar ── */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--border-default)' }}>
                {([
                    { key: 'senders' as const, Icon: Rocket, label: `互相预热（${warmupSenders.length}）` },
                    { key: 'pool' as const, Icon: Mail, label: `Outlook 账号池（${accounts.length}）` },
                    { key: 'import' as const, Icon: Package, label: "批量导入" },
                    { key: 'logs' as const, Icon: ClipboardList, label: `操作记录（${logs.length}）` },
                ]).map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} style={{
                        padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        background: 'transparent', border: 'none',
                        color: tab === t.key ? '#a5b4fc' : 'var(--text-dim)',
                        borderBottom: tab === t.key ? '2px solid #6366f1' : '2px solid transparent',
                        transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}>
                        <t.Icon size={14} /> {t.label}
                    </button>
                ))}
            </div>

            {tab === 'senders' && (
                <div style={{ ...card, marginBottom: 16 }}>
                    <h3 style={{ margin: '0 0 8px', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}><Rocket size={16} color="#a5b4fc" /> 参与互相预热的 Outlook 账号</h3>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                        {selectedBatchName} · {selectedAccounts.length} 个账号，其中 {warmupSenders.length} 个已配置 OAuth 且状态正常，可参与内部互发。发送时通过 Graph API 验证授权。
                    </p>
                    <input aria-label="搜索待预热账号" placeholder="搜索待预热邮箱…" value={senderSearch} onChange={e => setSenderSearch(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }} />
                    <div style={{ maxHeight: 480, overflow: 'auto' }}>
                        {visibleSenders.map(a => <div key={a.id} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '14px 12px', borderBottom: '1px solid var(--border-default)' }}>
                            <Mail size={18} color="#a5b4fc" />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{a.email}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>{a.import_batch_name || '未分批'} · 每日上限 {options.dailyLimit} 封</div>
                            </div>
                            <span style={{ fontSize: 11, color: '#a5b4fc' }}>已配置 OAuth</span>
                        </div>)}
                        {!visibleSenders.length && <p style={{ color: 'var(--text-dim)', padding: 20 }}>当前范围没有符合条件的待预热账号。</p>}
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 0 }}>所选范围内的账号互为发件方和收件方。实际发送结果可在“操作记录”中查看。</p>
                </div>
            )}

            {/* ── Pool Tab ── */}
            {tab === 'pool' && (
                <div style={{ ...card, marginBottom: 16 }}>
                    {/* Outreach capacity banner */}
                    {outlookCapacity && outlookCapacity.availableAccounts > 0 && (
                        <div style={{
                            marginBottom: 16, padding: '12px 16px', borderRadius: 10,
                            background: 'linear-gradient(135deg, rgba(56,189,248,0.08), rgba(99,102,241,0.06))',
                            border: '1px solid rgba(56,189,248,0.2)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ display: 'inline-flex', padding: 6, borderRadius: 8, background: 'rgba(56,189,248,0.15)' }}>
                                    <ArrowUpFromLine size={16} color="#38bdf8" />
                                </span>
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#38bdf8' }}>
                                        📬 共用账号池 — 同时用于对外发送
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                                        {outlookCapacity.availableAccounts} 个已授权账号可通过 Graph API 发送邮件；系统会按邮箱类型安排路由。
                                    </div>
                                </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{ fontSize: 20, fontWeight: 700, color: '#38bdf8' }}>{outlookCapacity.remainingCapacity}</div>
                                <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>封今日剩余额度</div>
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap', marginBottom: 14 }}>
                        <label style={{ flex: '1 1 280px', fontSize: 12 }}>按导入批次查看
                            <select aria-label="账号导入批次" value={batchFilter} onChange={e => setBatchFilter(e.target.value)} style={{ ...inputStyle, marginTop: 6 }}>
                                <option value="all">全部账号 · {accounts.length}</option>
                                <option value="legacy">历史账号 / 单个添加（未分批） · {legacyCount}</option>
                                {batches.map(b => <option key={b.id} value={b.id}>{b.name} · {b.account_count} 个 · {new Date(b.created_at).toLocaleString('zh-CN')}</option>)}
                            </select>
                        </label>
                        <label style={{ flex: '1 1 200px', fontSize: 12 }}>搜索邮箱
                            <input aria-label="搜索账号邮箱" placeholder="输入邮箱关键词" value={accountSearch} onChange={e => setAccountSearch(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                        </label>
                        <button className="btn btn-primary" onClick={() => { setImportResult(null); setTab('import'); }}>导入新批次</button>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 16 }}>
                        当前显示 {filteredAccounts.length} 个 · 已授权正常 {filteredAccounts.filter(a => a.status === 'active' && a.hasOAuthToken).length} 个 · 异常 {filteredAccounts.filter(a => a.status === 'error').length} 个
                        {batchFilter !== 'all' && <button style={{ ...smallBtn('#6366f1', '#a5b4fc'), marginLeft: 12 }} disabled={optionsLocked} onClick={() => { setOptions({ ...options, mode: 'outlook_pool', batchId: batchFilter }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>用于互相预热</button>}
                    </div>

                    {/* Quick add row */}
                    <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                        <input type="email" placeholder="邮箱" value={newEmail} onChange={e => setNewEmail(e.target.value)} style={{ ...inputStyle, flex: 2 }} />
                        <input type="password" placeholder="密码" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={{ ...inputStyle, flex: 2 }} />
                        <button type="submit" className="btn btn-primary" style={{ whiteSpace: 'nowrap', fontSize: 12, padding: '8px 14px' }}>+ 添加</button>
                        {addError && <span style={{ color: '#f87171', fontSize: 11 }}>{addError}</span>}
                    </form>

                    {filteredAccounts.length === 0 ? (
                        <p style={{ color: 'var(--text-dim)', fontSize: 13, textAlign: 'center', padding: 30 }}>
                            当前筛选下暂无账号。可切换批次、清空搜索或导入新批次。
                        </p>
                    ) : (
                        <div style={{ maxHeight: 480, overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                        <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>状态</th>
                                        <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>邮箱</th>
                                        <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>预热记录评分</th>
                                        <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>导入批次 / 添加时间</th>
                                        <th style={{ textAlign: 'right', padding: '6px 10px', color: 'var(--text-dim)', fontWeight: 500 }}>操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAccounts.map(acc => (
                                        <tr key={acc.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                            <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }} title={acc.last_error || ''}>{statusDot(acc.status)} <span style={{ marginLeft: 5 }}>{acc.status === 'error' ? '异常' : acc.hasOAuthToken && acc.status === 'active' ? '已授权' : '待授权'}</span></td>
                                            <td style={{ padding: '8px 10px', color: 'var(--text-primary)' }}>{acc.email}</td>
                                            <td style={{ padding: '8px 10px' }}>{scoreBar(acc.warmup_score)}</td>
                                            <td style={{ padding: '8px 10px', color: 'var(--text-dim)', fontSize: 11 }}>
                                                <div style={{ color: '#a5b4fc', marginBottom: 3 }}>{acc.import_batch_name || '未分批'}</div>{new Date(acc.created_at).toLocaleString('zh-CN')}
                                            </td>
                                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                                                <button onClick={() => handleTest(acc.id)} disabled={testing === acc.id}
                                                    style={{ ...smallBtn('#6366f1', '#a5b4fc'), marginRight: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                    {testing === acc.id ? '...' : <><Link2 size={10} /> 测试</>}
                                                </button>
                                                <button onClick={() => handleDelete(acc.id)} style={{ ...smallBtn('#f87171', '#f87171'), display: 'inline-flex', alignItems: 'center' }}><X size={10} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ── Import Tab ── */}
            {tab === 'import' && (
                <div style={{ ...card }}>
                    <h3 style={{ margin: '0 0 8px', fontSize: 15, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}><Package size={16} color="#a5b4fc" /> 批量导入 Outlook 账号</h3>
                    <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '0 0 12px' }}>
                        按以下格式粘贴账号： <code style={{ background: 'rgba(99,102,241,0.15)', padding: '2px 6px', borderRadius: 4, color: '#a5b4fc' }}>
                        email----password----clientId----oauthToken</code> （每行一个账号）
                    </p>
                    <label style={{ display: 'block', fontSize: 12, marginBottom: 14 }}>本次导入批次名称
                        <input aria-label="导入批次名称" value={batchName} maxLength={100} onChange={e => setBatchName(e.target.value)} placeholder="例如：9 月 9 日 · 第二批 Outlook（留空自动按时间命名）" style={{ ...inputStyle, marginTop: 6 }} />
                    </label>
                    <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>每次导入创建独立批次。重复账号会跳过，保留原批次；历史账号显示为“未分批”。</p>
                    <textarea
                        aria-label="待导入的 Outlook 账号"
                        value={importText}
                        onChange={e => setImportText(e.target.value)}
                        placeholder={'account@outlook.com----密码----Client ID----OAuth Token\n# 以 # 开头的行会被忽略'}
                        style={{
                            ...inputStyle, height: 180, resize: 'vertical', fontFamily: 'monospace', fontSize: 11,
                            lineHeight: 1.6, marginBottom: 12,
                        }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button onClick={handleBulkImport} disabled={importing || !importText.trim()}
                            className="btn btn-primary" style={{ fontSize: 12, padding: '8px 20px' }}>
                            {importing ? '正在导入...' : `导入 ${importText.split('\n').filter(l => l.trim() && !l.startsWith('#')).length} 个账号`}
                        </button>
                        {importResult && (
                            <span style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ color: '#6ee7b7', display: 'inline-flex', alignItems: 'center', gap: 3 }}><CheckCircle2 size={12} /> {importResult.imported} 个已导入</span>
                                {importResult.skipped > 0 && <span style={{ color: '#fbbf24', marginLeft: 8 }}>{importResult.skipped} 个重复</span>}
                                {importResult.errors.length > 0 && <span style={{ color: '#f87171', marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 3 }}><XCircle size={12} /> {importResult.errorCount || importResult.errors.length} 项错误</span>}
                            </span>
                        )}
                    </div>
                    {importResult?.batch && <p style={{ fontSize: 12, color: '#a5b4fc' }}>
                        已保存批次：{importResult.batch.name}
                        <button style={{ ...smallBtn('#6366f1', '#a5b4fc'), marginLeft: 12 }} onClick={() => { setBatchFilter(importResult.batch!.id); setAccountSearch(''); setTab('pool'); }}>查看本批账号</button>
                    </p>}
                    {importResult && importResult.errors.length > 0 && (
                        <div style={{ marginTop: 12, padding: 10, background: 'rgba(248,113,113,0.08)', borderRadius: 6, fontSize: 11, color: '#f87171' }}>
                            {importResult.errors.map((e, i) => <div key={i}>{e}</div>)}
                        </div>
                    )}
                </div>
            )}

            {/* ── Logs Tab ── */}
            {tab === 'logs' && (
                <div style={{ ...card }}>
                    {logs.length === 0 ? (
                        <p style={{ color: 'var(--text-dim)', fontSize: 13, textAlign: 'center', padding: 20 }}>暂无预热记录。</p>
                    ) : (
                        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                            {logs.map(log => {
                                const time = new Date(log.created_at).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
                                const date = new Date(log.created_at).toLocaleDateString('en', { month: 'short', day: 'numeric' });
                                return (
                                    <div key={log.id} style={{
                                        display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0',
                                        borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 12,
                                    }}>
                                        <span style={{ color: 'var(--text-dim)', width: 75, flexShrink: 0, fontSize: 11 }}>{date} {time}</span>
                                        <span style={{ width: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><ActionIcon action={log.action} /></span>
                                        <span style={{
                                            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                                            background: `${ACTION_COLORS[log.action] || '#94a3b8'}22`,
                                            color: ACTION_COLORS[log.action] || '#94a3b8',
                                            textTransform: 'uppercase', width: 54, textAlign: 'center', flexShrink: 0,
                                        }}>{ACTION_LABELS[log.action] || log.action}</span>
                                        <span style={{ color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {log.details || log.sender_email}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}


        </div>
    );
}

